/**
 * PaginationTestPage - Test harness for EdgeDocsVirtualPages
 * 
 * Route: /dev/edgedocs/pagination-test
 * 
 * Features:
 * - Load sample documents (small, medium, large)
 * - Test caret positioning
 * - Test selection
 * - Performance metrics
 */

import React, { useState, useEffect } from 'react';
import { EdgeDocsVirtualPagesV3 } from '../editors/edgedocs/EdgeDocsVirtualPages.V3';
import { PAGE_SIZES, type PageSetup } from '../core/types/Typesetter';
import type { DocumentState } from '../editors/edgedocs/EdgeDocsTypes';

// ============================================================================
// Sample Documents
// ============================================================================

const SAMPLE_DOCUMENTS = {
  small: `Hello, world!

This is a small test document with a few paragraphs.

Each paragraph is separated by blank lines.

Lorem ipsum dolor sit amet, consectetur adipiscing elit.`,

  medium: Array(10).fill(null).map((_, i) => 
    `Paragraph ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.\n\nDuis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`
  ).join('\n\n'),

  // 10-PAGE TEST DOCUMENT (~50 paragraphs, ~5 paragraphs per page)
  // Each paragraph is ~400-600 chars to ensure proper pagination
  // Used for testing DELETE key behavior at scale
  tenPage: Array(50).fill(null).map((_, i) => {
    const pageNum = Math.floor(i / 5) + 1;
    const paraNum = (i % 5) + 1;
    const isHeading = paraNum === 1;
    
    if (isHeading) {
      return `SECTION ${pageNum}: LEGAL PROVISIONS AND TERMS OF AGREEMENT

This section establishes the fundamental legal framework governing the relationship between all parties. The terms contained herein shall be binding upon execution and shall remain in full force and effect unless modified by mutual written consent.`;
    }
    
    const variations = [
      `${pageNum}.${paraNum} DEFINITIONS AND INTERPRETATION. For purposes of this Agreement, the following definitions shall apply: (a) "Agreement" means this document and all exhibits attached hereto; (b) "Parties" means all signatories to this document; (c) "Effective Date" means the date of final execution by all parties; (d) "Term" means the period during which this Agreement remains in effect. All terms defined in this section shall have the meanings ascribed to them when used throughout this Agreement.`,
      
      `${pageNum}.${paraNum} REPRESENTATIONS AND WARRANTIES. Each party hereby represents and warrants to the other parties that: (i) it has full power and authority to enter into this Agreement; (ii) the execution and performance of this Agreement has been duly authorized; (iii) this Agreement constitutes a valid and binding obligation; (iv) the performance of obligations under this Agreement will not conflict with any other agreements. These representations shall survive the execution of this Agreement.`,
      
      `${pageNum}.${paraNum} OBLIGATIONS AND COVENANTS. During the Term of this Agreement, each party shall: (A) comply with all applicable laws and regulations; (B) maintain accurate records of all transactions; (C) provide timely notice of any material changes; (D) cooperate in good faith to resolve any disputes that may arise. Failure to comply with these obligations may result in termination as provided herein.`,
      
      `${pageNum}.${paraNum} LIMITATION OF LIABILITY. IN NO EVENT SHALL ANY PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT. THE TOTAL LIABILITY OF ANY PARTY SHALL NOT EXCEED THE AMOUNTS PAID UNDER THIS AGREEMENT DURING THE TWELVE MONTHS PRECEDING THE CLAIM.`,
    ];
    
    return variations[(paraNum - 2) % 4];
  }).join('\n\n'),

  large: Array(100).fill(null).map((_, i) => 
    `Page ${Math.floor(i / 2) + 1}, Section ${i % 2 + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.\n\nSed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.`
  ).join('\n\n'),
};

// ============================================================================
// Helper: Generate paragraphs array from text
// ============================================================================

/**
 * Splits text into paragraphs using \n\n as delimiter.
 * Returns an array of paragraph objects compatible with EdgeDocsVirtualPagesV3.
 * Uses 'text' as the LegalParagraphType (generic paragraph).
 */
function textToParagraphs(text: string): Array<{
  id: string;
  type: 'text';  // LegalParagraphType literal
  version: number;
  textStart: number;
  textEnd: number;
}> {
  if (!text || text.length === 0) {
    return [{
      id: 'para-0',
      type: 'text' as const,
      version: 1,
      textStart: 0,
      textEnd: 0,
    }];
  }

  const paragraphs: Array<{
    id: string;
    type: 'text';
    version: number;
    textStart: number;
    textEnd: number;
  }> = [];

  // Split on \n\n (paragraph delimiter)
  let paraIndex = 0;
  
  // Find all \n\n occurrences
  const regex = /\n\n/g;
  let match;
  let lastEnd = 0;
  
  while ((match = regex.exec(text)) !== null) {
    // Paragraph content is from lastEnd to match.index
    // Include the first \n as part of the paragraph, second \n is delimiter
    const paraEnd = match.index + 1; // Include first \n
    
    paragraphs.push({
      id: `para-${paraIndex}`,
      type: 'text' as const,
      version: 1,
      textStart: lastEnd,
      textEnd: paraEnd,
    });
    
    paraIndex++;
    lastEnd = match.index + 2; // Skip both \n characters
  }
  
  // Add final paragraph (from lastEnd to end of text)
  if (lastEnd <= text.length) {
    paragraphs.push({
      id: `para-${paraIndex}`,
      type: 'text' as const,
      version: 1,
      textStart: lastEnd,
      textEnd: text.length,
    });
  }
  
  console.log(`[PaginationTestPage] Generated ${paragraphs.length} paragraphs from text of length ${text.length}`);
  
  return paragraphs;
}

// ============================================================================
// Main Component
// ============================================================================

export default function PaginationTestPage() {
  const initialText = SAMPLE_DOCUMENTS.small;
  const [documentState, setDocumentState] = useState<DocumentState & { paragraphs?: any[] }>({
    text: initialText,
    cursorPosition: 0,
    selection: { anchor: 0, focus: 0 },
    paragraphs: textToParagraphs(initialText),
  });

  const [pageSetup, setPageSetup] = useState<PageSetup>(PAGE_SIZES.LETTER);
  const [selectedDoc, setSelectedDoc] = useState<'small' | 'medium' | 'tenPage' | 'large'>('small');
  const DEFAULT_FONT_FAMILY = 'EdgeDocs Roman';

  // ========================================
  // Static Test Page (No Worker)
  // ========================================

  // Ensure the test font is present in document.fonts so EdgeDocsVirtualPages unblocks
  useEffect(() => {
    // Best-effort load; if already available, this is a no-op
    try { (document as any).fonts?.load?.(`${14}px "${DEFAULT_FONT_FAMILY}"`); } catch {}
  }, []);

  // ========================================
  // Event Handlers
  // ========================================

  const handleCursorChange = (position: number) => {
    console.log(`[PaginationTestPage] Cursor moved to position ${position}`);
    setDocumentState(prev => ({ ...prev, cursorPosition: position }));
  };

  const handleSelectionChange = (selection: { anchor: number; focus: number }) => {
    console.log(`[PaginationTestPage] Selection changed:`, selection);
    setDocumentState(prev => ({ ...prev, selection }));
  };

  const handleDocumentChange = (docType: 'small' | 'medium' | 'tenPage' | 'large') => {
    console.log(`[PaginationTestPage] Loading ${docType} document...`);
    const newText = SAMPLE_DOCUMENTS[docType];
    setSelectedDoc(docType);
    setDocumentState({
      text: newText,
      cursorPosition: 0,
      selection: { anchor: 0, focus: 0 },
      paragraphs: textToParagraphs(newText),
    });
  };

  const handlePageSetupChange = (setup: 'LETTER' | 'A4' | 'LEGAL') => {
    console.log(`[PaginationTestPage] Changing page setup to ${setup}`);
    setPageSetup(PAGE_SIZES[setup]);
  };

  // ========================================
  // Render
  // ========================================

  // Always ready for static test page

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-200">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-300 px-6 py-3 flex items-center gap-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-800">Pagination Test Lab</h1>
        
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Document:</label>
          <select
            value={selectedDoc}
            onChange={(e) => handleDocumentChange(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-lex-accent"
          >
            <option value="small">Small (~5 lines)</option>
            <option value="medium">Medium (~5 pages)</option>
            <option value="tenPage">10-Page Legal (~50 paragraphs)</option>
            <option value="large">Large (~50 pages)</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Page Size:</label>
          <select
            value={Object.keys(PAGE_SIZES).find(k => PAGE_SIZES[k as keyof typeof PAGE_SIZES] === pageSetup) || 'LETTER'}
            onChange={(e) => handlePageSetupChange(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-lex-accent"
          >
            <option value="LETTER">Letter (8.5" × 11")</option>
            <option value="A4">A4 (210mm × 297mm)</option>
            <option value="LEGAL">Legal (8.5" × 14")</option>
          </select>
        </div>

        <div className="ml-auto flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Cursor: <span className="font-mono font-medium">{documentState.cursorPosition}</span>
          </div>
          <div className="text-sm text-gray-600">
            Selection: 
            <span className="font-mono font-medium ml-1">
              {documentState.selection.anchor === documentState.selection.focus 
                ? 'None' 
                : `[${Math.min(documentState.selection.anchor, documentState.selection.focus)}, ${Math.max(documentState.selection.anchor, documentState.selection.focus)})`
              }
            </span>
          </div>
          <div className="text-sm text-gray-600">
            Length: <span className="font-mono font-medium">{documentState.text.length}</span>
          </div>
        </div>
      </div>

      {/* Virtual Page Canvases */}
      <div className="flex-1 overflow-hidden">
        <EdgeDocsVirtualPagesV3
          documentState={documentState}
          zoomMode="fit"
          userZoom={1}
          pageWidth={816}  // US Letter width at 96 DPI
          pageHeight={1056} // US Letter height at 96 DPI
        />
      </div>

      {/* Instructions */}
      <div className="bg-white border-t border-gray-300 px-6 py-3 text-sm text-gray-600">
        <strong>Instructions:</strong> 
        <span className="ml-2">Click to position cursor. Drag to select text. Scroll to load more pages. Try different document sizes and page setups.</span>
      </div>
    </div>
  );
}

